import { useState, useEffect } from 'react'
import { Button, MenuItem, Typography } from '@mui/material'
import Iconify from '@/components/Iconify'
import MenuPopover from '@/components/MenuPopover'
import { ProductsOrder } from '@/generated/graphql'

export enum ChromeExtensionPromptsOrder {
  UsagesDesc = 'usages_DESC',
  UsagesAsc = 'usages_ASC',
  LikesDesc = 'likes_DESC',
  LikesAsc = 'likes_ASC',
  ViewsAsc = 'views_ASC',
  ViewsDesc = 'views_DESC',
  CreatedAtAsc = 'createdAt_ASC',
  CreatedAtDesc = 'createdAt_DESC',
}

export enum ShopProductSortForPage {
  product = 'product',
  chromeExtensionPrompt = 'chromeExtensionPrompt',
}

type ProductOption = {
  forType: ShopProductSortForPage.product
  label: string
  value: ProductsOrder
}
type ChromeExtensionPromptOption = {
  forType: ShopProductSortForPage.chromeExtensionPrompt
  label: string
  value: ChromeExtensionPromptsOrder
}

type Option = ChromeExtensionPromptOption | ProductOption

const SORT_BY_OPTIONS: ProductOption[] = [
  {
    value: ProductsOrder.SysPublishedAtDesc,
    label: 'Newest',
    forType: ShopProductSortForPage.product,
  },
  {
    value: ProductsOrder.SysPublishedAtAsc,
    label: 'Oldest',
    forType: ShopProductSortForPage.product,
  },
  {
    value: ProductsOrder.TitleAsc,
    label: 'Name: A-Z',
    forType: ShopProductSortForPage.product,
  },
  {
    value: ProductsOrder.TitleDesc,
    label: 'Name: Z-A',
    forType: ShopProductSortForPage.product,
  },
]
const SORT_BY_FOR_CHROME_EXTENSION_PAGE_OPTIONS: ChromeExtensionPromptOption[] = [
  {
    value: ChromeExtensionPromptsOrder.UsagesDesc,
    label: 'Usages Desc',
    forType: ShopProductSortForPage.chromeExtensionPrompt,
  },
  {
    value: ChromeExtensionPromptsOrder.UsagesAsc,
    label: 'Usages Asc',
    forType: ShopProductSortForPage.chromeExtensionPrompt,
  },
  {
    value: ChromeExtensionPromptsOrder.LikesDesc,
    label: 'Likes Desc',
    forType: ShopProductSortForPage.chromeExtensionPrompt,
  },
  {
    value: ChromeExtensionPromptsOrder.LikesAsc,
    label: 'Likes Asc',
    forType: ShopProductSortForPage.chromeExtensionPrompt,
  },
  {
    value: ChromeExtensionPromptsOrder.ViewsDesc,
    label: 'Views Desc',
    forType: ShopProductSortForPage.chromeExtensionPrompt,
  },
  {
    value: ChromeExtensionPromptsOrder.ViewsAsc,
    label: 'Views Asc',
    forType: ShopProductSortForPage.chromeExtensionPrompt,
  },
  {
    value: ChromeExtensionPromptsOrder.CreatedAtAsc,
    label: 'Newest',
    forType: ShopProductSortForPage.chromeExtensionPrompt,
  },
  {
    value: ChromeExtensionPromptsOrder.CreatedAtDesc,
    label: 'Oldest',
    forType: ShopProductSortForPage.chromeExtensionPrompt,
  },
]

function renderLabelForProduct(value: ProductsOrder) {
  const foundedOption = SORT_BY_OPTIONS.find((option) => option.value === value)
  return foundedOption?.label || SORT_BY_OPTIONS[0].label
}
function renderLabelForChrome(value: ChromeExtensionPromptsOrder) {
  const foundedOption = SORT_BY_FOR_CHROME_EXTENSION_PAGE_OPTIONS.find(
    (option) => option.value === value
  )
  return foundedOption?.label || SORT_BY_FOR_CHROME_EXTENSION_PAGE_OPTIONS[0].label
}

type OnSortForProduct = (value: ProductsOrder) => void
type OnSortForChromeExtensionPrompt = (value: ChromeExtensionPromptsOrder) => void

type PropsForProduct = {
  forPage: ShopProductSortForPage.product
  value?: ProductsOrder
  onSort?: OnSortForProduct
  defaultValue?: ProductsOrder
}

type PropsForChromeExtensionPrompt = {
  forPage: ShopProductSortForPage.chromeExtensionPrompt
  value?: ChromeExtensionPromptsOrder
  onSort?: OnSortForChromeExtensionPrompt
  defaultValue?: ChromeExtensionPromptsOrder
}

type Props = PropsForProduct | PropsForChromeExtensionPrompt

const DEFAULT_PRODUCTS_ORDERS = ProductsOrder.SysPublishedAtDesc

export default function ShopProductSort(props: Props) {
  const { value, onSort, forPage = ShopProductSortForPage.product, defaultValue } = props
  const [sortBy, setSortBy] = useState<typeof defaultValue | any>(undefined)
  const [open, setOpen] = useState<HTMLButtonElement | null>(null)

  const handleOpen = (currentTarget: HTMLButtonElement) => {
    setOpen(currentTarget)
  }

  const handleClose = () => {
    setOpen(null)
  }

  let options: Option[] = []
  if (forPage === ShopProductSortForPage.product) {
    options = SORT_BY_OPTIONS
  }
  if (forPage === ShopProductSortForPage.chromeExtensionPrompt) {
    options = SORT_BY_FOR_CHROME_EXTENSION_PAGE_OPTIONS
  }

  const handleSortBy = (value: typeof sortBy) => {
    const url = new URL(window.location.href)
    if (value) {
      if (value === DEFAULT_PRODUCTS_ORDERS) {
        url.searchParams.delete('sort')
      } else {
        url.searchParams.set('sort', value)
      }
    } else {
      url.searchParams.delete('sort')
    }
    window.history.replaceState(null, '', url.toString())
    handleClose()
    setSortBy(value)
    if (value) {
      if (props.forPage === ShopProductSortForPage.chromeExtensionPrompt) {
        const onSortChrome = onSort as OnSortForChromeExtensionPrompt
        onSortChrome(value as ChromeExtensionPromptsOrder)
      }
      if (props.forPage === ShopProductSortForPage.product) {
        const onSortProduct = onSort as OnSortForProduct
        onSortProduct(value as ProductsOrder)
      }
    }
  }

  useEffect(() => {
    if (value !== sortBy) {
      if (value) {
        setTimeout(() => setSortBy(value), 100)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <>
      <Button
        fullWidth
        color="inherit"
        variant="outlined"
        size="large"
        disableRipple
        onClick={(event) => handleOpen(event.currentTarget)}
        endIcon={<Iconify icon={open ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} />}
        sx={{ display: 'inline-flex' }}
      >
        <Typography variant="subtitle2">Sort By:&nbsp;</Typography>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
          {sortBy &&
            props.forPage === ShopProductSortForPage.chromeExtensionPrompt &&
            renderLabelForChrome(sortBy)}
          {sortBy &&
            props.forPage === ShopProductSortForPage.product &&
            renderLabelForProduct(sortBy)}
        </Typography>
      </Button>

      <MenuPopover
        anchorEl={open}
        open={Boolean(open)}
        onClose={handleClose}
        sx={{
          width: 'auto',
          '& .MuiMenuItem-root': { typography: 'body2', borderRadius: 0.75 },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === sortBy}
            onClick={() => {
              if (option.forType === ShopProductSortForPage.product) {
                handleSortBy(option.value)
              }
              if (option.forType === ShopProductSortForPage.chromeExtensionPrompt) {
                handleSortBy(option.value)
              }
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </MenuPopover>
    </>
  )
}
